import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { getHdPlanetDisplayConfig, HdPlanetKey } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdPlanetInfoService, HdPlanetInfluenceTranslations } from '@/app/human-design/chart/bodygraph/hd-planet-info.service';

/**
 * Volgorde van de planeten in de invloed-tabel — gebaseerd op afstand tot
 * de aarde (Sun/Earth/Moon → nodes → inner-personal → outer-social → outer-trans).
 * Chiron is bewust uitgesloten (geen invloed-data).
 */
const HD_PLANET_INFLUENCE_ORDER: readonly HdPlanetKey[] = [
  'sun', 'earth', 'moon',
  'northNode', 'southNode',
  'mercury', 'venus', 'mars',
  'jupiter', 'saturn',
  'uranus', 'neptune', 'pluto',
];
import {
  parsePlanetInfluenceMarkers,
  HdPlanetInfluenceSegment,
} from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-influence-markers.util';

interface InfluenceRowVm {
  readonly planetKey: string;
  readonly planetLabel: string;
  readonly astronomiconChar: string;
  readonly stripeColor: string;
  readonly typeLabel: string;
  readonly essence: string;
  readonly ravePsychology: string;
  readonly phs: string;
  readonly inDefinedChannel: string;
  readonly inDefinedCenter: string;
  readonly inUndefinedCenter: string;
  readonly influenceOnSegments: readonly HdPlanetInfluenceSegment[];
}

/**
 * Pagina die de planeet-invloed cheat-sheet rendert: tabel met één rij per
 * planeet (Chiron uitgesloten — geen invloed-data, zie spec) en kolommen voor
 * essence, rave-psychology, PHS, in defined channel/center/undefined center,
 * en de gemarkeerde influence-on tekst.
 */
@Component({
  selector: 'hd-planet-influence-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-planet-influence-cheat-sheet-page.component.html',
  styleUrl: './hd-planet-influence-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdPlanetInfluenceCheatSheetPageComponent {
  private readonly planetInfoService = inject(HdPlanetInfoService);

  protected readonly rows = computed<readonly InfluenceRowVm[]>(() => {
    const allInfluences = this.planetInfoService.planetInfluences();
    const planetTranslations = this.planetInfoService.planetTranslations();
    const result: InfluenceRowVm[] = [];

    for (const key of HD_PLANET_INFLUENCE_ORDER) {
      const influence: HdPlanetInfluenceTranslations | undefined = allInfluences[key];
      if (!influence) continue;
      const config = getHdPlanetDisplayConfig(key);
      result.push({
        planetKey: config.key,
        planetLabel: planetTranslations[config.key]?.name ?? config.label,
        astronomiconChar: config.astronomiconChar,
        stripeColor: config.stripeColor,
        typeLabel: config.type,
        essence: influence.essence,
        ravePsychology: influence.ravePsychology,
        phs: influence.phs,
        inDefinedChannel: influence.inDefinedChannel,
        inDefinedCenter: influence.inDefinedCenter,
        inUndefinedCenter: influence.inUndefinedCenter,
        influenceOnSegments: parsePlanetInfluenceMarkers(influence.influenceOn),
      });
    }
    return result;
  });
}
